import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import authApi from "../../../../service/IdApi";
import { useAppContext } from "../../../../context/appContext";

const STATUS_SEVERITY = {
    active: "info",
    completed: "success",
    snoozed: "warning",
    dismissed: "danger",
};

const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString();
};

const formatDateTime = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
};

const resolveNextDueLabel = (nextDueAt) => {
    if (!nextDueAt) {
        return { text: "—", overdue: false };
    }
    const due = new Date(nextDueAt);
    if (isNaN(due.getTime())) {
        return { text: "—", overdue: false };
    }
    const overdue = due.getTime() <= Date.now();
    return {
        text: formatDateTime(nextDueAt) + (overdue ? " (due now)" : ""),
        overdue,
    };
};

const resolveLastDripDescription = (emailPreview) => {
    if (!emailPreview) {
        return "—";
    }
    const count = emailPreview.siteRecommendationEmailCount || 0;
    if (count <= 0 && !emailPreview.lastSiteRecommendationEmailAt) {
        return "None sent yet";
    }
    if (emailPreview.lastEmailedRecommendationTitle) {
        return emailPreview.lastEmailedRecommendationTitle;
    }
    if (emailPreview.siteConvertedFromProfile && count >= 1) {
        return "Site conversion (or earlier drip)";
    }
    if (count >= 1) {
        return "Drip email sent (tip details unavailable)";
    }
    return "—";
};

const resolveNextEmailDescription = (emailPreview) => {
    if (!emailPreview) {
        return "—";
    }
    if (!emailPreview.siteEnabled || !emailPreview.siteConvertedAt) {
        return "Not enrolled (profile is not a site)";
    }
    if (emailPreview.emailKind === "site_conversion") {
        return emailPreview.title || "Site conversion";
    }
    if (emailPreview.recommendation?.title) {
        return emailPreview.recommendation.title;
    }
    if (emailPreview.title) {
        return emailPreview.title;
    }
    return "No eligible emailable tip right now";
};

const ProviderRecommendationsDialog = ({ visible, onHide, providerId, providerName }) => {
    const { displayAlert } = useAppContext();
    const [recommendations, setRecommendations] = useState(null);
    const [users, setUsers] = useState([]);
    const [emailPreview, setEmailPreview] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!visible || !providerId) return;
        let cancelled = false;
        setIsLoading(true);
        setRecommendations(null);
        setUsers([]);
        setEmailPreview(null);
        Promise.all([
            authApi.get("admin/recommendations/providers/" + encodeURIComponent(providerId)),
            authApi.post("admin/provider/users", { providerId }),
            authApi.get(
                "admin/recommendations/providers/" + encodeURIComponent(providerId) + "/email/preview"
            ),
        ])
            .then(([recommendationsResult, usersResult, emailPreviewResult]) => {
                if (!cancelled) {
                    setRecommendations(recommendationsResult.data.recommendations || []);
                    setUsers(usersResult.data.users || []);
                    setEmailPreview(emailPreviewResult.data || null);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    displayAlert("error", "Error", "Failed to load provider details");
                    console.error(err);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => { cancelled = true; };
    }, [visible, providerId]);

    const emailBodyTemplate = (rowData) => {
        if (!rowData.email) return "—";
        return (
            <a href={`/account/admin/user-events?email=${rowData.email}`}>
                {rowData.email}
            </a>
        );
    };

    const statusBodyTemplate = (rowData) => {
        const status = rowData.status;
        const severity = STATUS_SEVERITY[status] || null;
        return <Tag value={status || "—"} severity={severity} />;
    };

    const effortBodyTemplate = (rowData) => `${rowData.value} / ${rowData.priority} / ${rowData.effort}`;

    const dateBodyTemplate = (rowData) => {
        if (rowData.status === "snoozed") return formatDate(rowData.snoozedUntil);
        if (rowData.status === "completed") return formatDate(rowData.completedAt);
        if (rowData.status === "dismissed") return formatDate(rowData.dismissedAt);
        return "—";
    };

    const prerequisitesBodyTemplate = (rowData) => {
        if (!rowData.prerequisites || rowData.prerequisites.length === 0) return "—";
        return rowData.prerequisites.join(", ");
    };

    if (!visible) return null;

    const nextDue = resolveNextDueLabel(emailPreview?.nextSiteRecommendationEmailAt);
    const recipientEmails = Array.isArray(emailPreview?.recipientEmails)
        ? emailPreview.recipientEmails.filter(Boolean)
        : (emailPreview?.recipientEmail ? [emailPreview.recipientEmail] : []);

    return (
        <Dialog
            visible={visible}
            style={{ width: "70vw" }}
            breakpoints={{ "1280px": "85vw", "641px": "95vw" }}
            header={providerName ? `Recommendations - ${providerName}` : "Recommendations"}
            modal
            onHide={onHide}
        >
            {isLoading ? (
                <div className="flex justify-content-center py-4">
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }} />
                </div>
            ) : recommendations ? (
                <>
                    <div className="field grid mb-2">
                        <label className="col-fixed font-semibold" style={{ width: "150px" }}>Email Drip</label>
                        <div className="col">
                            <div className="surface-ground border-round p-3">
                                <div className="grid">
                                    <div className="col-12 md:col-6">
                                        <div className="text-sm text-color-secondary mb-1">Subscribed</div>
                                        <div>
                                            {emailPreview?.siteRecommendationsEmailEnabled === false ? (
                                                <Tag value="No" severity="danger" />
                                            ) : (
                                                <Tag value="Yes" severity="success" />
                                            )}
                                            {!emailPreview?.siteEnabled && (
                                                <span className="ml-2 text-color-secondary text-sm">
                                                    (not a site)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="text-sm text-color-secondary mb-1">Drip emails sent</div>
                                        <div>{emailPreview?.siteRecommendationEmailCount ?? 0}</div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="text-sm text-color-secondary mb-1">Last drip sent</div>
                                        <div>{formatDateTime(emailPreview?.lastSiteRecommendationEmailAt)}</div>
                                        <div className="text-sm text-color-secondary mt-1">
                                            {resolveLastDripDescription(emailPreview)}
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="text-sm text-color-secondary mb-1">Next drip due</div>
                                        <div className={nextDue.overdue ? "font-semibold text-orange-600" : ""}>
                                            {nextDue.text}
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="text-sm text-color-secondary mb-1">Next email</div>
                                        <div className="font-medium">{resolveNextEmailDescription(emailPreview)}</div>
                                        {emailPreview?.emailKind === "site_conversion" && (
                                            <div className="text-sm text-color-secondary mt-1">
                                                Profile-to-site conversion drip email
                                                {emailPreview.siteConvertedFromProfile ? " (converted from profile)" : ""}.
                                            </div>
                                        )}
                                        {emailPreview?.recommendation?.description && (
                                            <div className="text-sm text-color-secondary mt-1">
                                                {emailPreview.recommendation.description}
                                            </div>
                                        )}
                                        {recipientEmails.length > 0 && (
                                            <div className="text-sm text-color-secondary mt-2">
                                                Recipients: {recipientEmails.join(", ")}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="field grid mb-2">
                        <label className="col-fixed font-semibold" style={{ width: "150px" }}>Account Users</label>
                        <div className="col text-color-secondary">
                            Recommendation emails are sent to Promote admins first, then any account admins or users if none are found. Unclaimed sites use the profile email when available.
                        </div>
                    </div>
                    <DataTable
                        value={users}
                        dataKey="email"
                        size="small"
                        stripedRows
                        className="p-datatable-sm mb-4"
                        emptyMessage="No users on this account."
                    >
                        <Column field="userId" header="ID" sortable />
                        <Column field="email" header="Email" body={emailBodyTemplate} sortable />
                        <Column field="createdAt" header="Created On" sortable />
                    </DataTable>
                    <div className="field grid mb-2">
                        <label className="col-fixed font-semibold" style={{ width: "150px" }}>Recommendations</label>
                    </div>
                    <DataTable
                        value={recommendations}
                        size="small"
                        stripedRows
                        className="p-datatable-sm"
                        emptyMessage="No recommendations for this provider."
                    >
                        <Column field="title" header="Title" />
                        <Column
                            field="product"
                            header="Product"
                            body={(rowData) => rowData.productFriendlyName || rowData.product || "—"}
                        />
                        <Column header="Status" body={statusBodyTemplate} />
                        <Column field="score" header="Score" />
                        <Column header="Value / Priority / Effort" body={effortBodyTemplate} />
                        <Column header="Date" body={dateBodyTemplate} />
                        <Column header="Emailed At" body={(rowData) => formatDate(rowData.emailedAt)} />
                        <Column header="Prerequisites" body={prerequisitesBodyTemplate} />
                    </DataTable>
                </>
            ) : null}
        </Dialog>
    );
};

export default ProviderRecommendationsDialog;
